/* THE GAME'S EARS, LIFTED OUT OF THE GAME.
 *
 * `src/game.ts` was 4,535 lines and one class owned area loading, collision,
 * saves, the shop, spawning, puzzles, eleven draw methods AND the whole Web
 * Audio stack. An adversarial reviewer scored `structure` 4 out of 10 and made
 * the argument concretely rather than as taste: the shop's sold-out guard
 * lives eleven hundred lines from the write that sets the flag it reads, so
 * nobody reviewing either half ever sees the pair — and both of those halves
 * were, in fact, an exploit that survived the entire 769-check suite.
 *
 * This is the first cut and the easiest: the audio block was already
 * contiguous at the end of the file and touches nothing but its own graph and
 * a handful of fields for deciding what should be playing. Every function
 * takes the game as its first argument and `Game` keeps a one-line delegator,
 * so no call site anywhere else had to change and the diff stays reviewable.
 */
import { UI, T, TS } from './art.ts';
import { COLS, ROWS } from './world.ts';
import { FROST_LINE, SCORCH_LINE } from './sim/fields.ts';
import { RNG, mix } from './rng.ts';
import { ambienceFor } from './ambience.ts';
import { TRACKS } from './music-data.ts';
import { Music } from './music.ts';

/** How many frames a track has to be wanted before it takes over. A spawn or
 *  a scroll can flip `trackFor()` for a frame or two, and restarting the
 *  jukebox on that reads as a stutter. Lives here now, with its only two
 *  readers; `game.js` re-exports it so the suite's import still resolves. */
export const TRACK_HOLD = 8;
/** Frames the boss piece stays after a beast last showed: three seconds, so a beast pacing the edge of the window does not flip the score. */
export const BOSS_HOLD = 180;

export function ac(game) {
  if (game.muted) return null;
  if (!game.audio) {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    game.audio = new C();
    // a limiter, because music and effects sum freely and a blast during a
    // busy bar used to clip
    game.limiter = game.audio.createDynamicsCompressor();
    game.limiter.threshold.value = -12;
    game.limiter.ratio.value = 12;
    game.limiter.attack.value = 0.003;
    game.bus = game.audio.createGain();
    game.bus.gain.value = 0.7;
    // A MASTER CEILING. The effects limiter and the music guard both ran
    // straight to `destination`, so nothing anywhere looked at their SUM: a
    // routine parry over a busy bar measured +1.23 dBFS, which is a click.
    // Brick-wall, after the merge, doing nothing until the sum asks for it.
    game.master = game.audio.createDynamicsCompressor();
    game.master.threshold.value = -1;
    game.master.knee.value = 0;
    game.master.ratio.value = 20;
    game.master.attack.value = 0.001;
    game.master.release.value = 0.1;
    game.master.connect(game.audio.destination);
    game.bus.connect(game.limiter);
    game.limiter.connect(game.master);
    // Music gets its own path. Sharing the effects limiter meant the score
    // sat 0.6 dB into permanent gain reduction with nothing else playing —
    // it was compressing the music, not catching transients — and every
    // blast ducked the soundtrack for a quarter of a second. This guard is
    // a safety net at -3 dB, not a mix bus.
    game.musicBus = game.audio.createGain();
    // 0.9, NOT 1.25. The tracks come out of the mixdown at -0.32 to -0.71
    // dBFS — already at full scale — so a 1.25 bus put three of the five
    // OVER zero before a single effect played: wilds +1.48, hymn +1.61, boss
    // +1.23 dBFS. The guard below then held them down at 0.43 dB of mean
    // gain reduction with nothing else in the mix, which is the exact fault
    // its own comment says it was written to fix. A safety net that is
    // always catching something is a mix bus with a bad name on it.
    game.musicBus.gain.value = 0.9;
    game.musicGuard = game.audio.createDynamicsCompressor();
    game.musicGuard.threshold.value = -3;
    game.musicGuard.ratio.value = 6;
    game.musicGuard.knee.value = 6;
    game.musicGuard.attack.value = 0.005;
    game.musicGuard.release.value = 0.12;
    /* DURESS (the music lens, round 42): at a quarter of your life the score goes under a lowpass, so the music knows one heart from eight. No note changes. */
    game.musicDuress = game.audio.createBiquadFilter(); game.musicDuress.type = 'lowpass'; game.musicDuress.frequency.value = 12000; game.musicDuressHz = 12000;
    game.musicBus.connect(game.musicDuress); game.musicDuress.connect(game.musicGuard);
    game.musicGuard.connect(game.master);
    applyVolumes(game);
  }
  // Only on a gesture, and only one in flight. Gating on the gesture alone
  // just relocated the pile-up behind the first touch: measured, 61 resume()
  // calls in 60 frames once the flag was set, and "suspended after a
  // gesture" is exactly the state iOS hands back after a call or Siri.
  if (game.audio.state === 'suspended' && game.gestured && !game.resuming) {
    game.resuming = true;
    Promise.resolve(game.audio.resume())
      .then(() => { game.resuming = false; })
      .catch(() => { game.resuming = false; game.gestured = false; });
  }
  return game.audio;
}

/** White noise through a sweeping lowpass: the only way to make an
 *  explosion, a scrape or a thud sound like anything but a synth tone. */
/* THE SAME CUE, NEVER THE SAME SOUND.
 *
 * All nineteen cues were sample-identical on every firing — verified by
 * rendering each into two fresh contexts and differencing: zero for ten of
 * eleven tested, 7.5e-9 for the eleventh. One seeded 1.2s noise buffer, always
 * started at offset 0, and no oscillator ever detuned. `swing` fires on every
 * attack, hundreds of times a run, as the same 90ms of the same noise.
 *
 * A counter, not `Math.random`: this project proves determinism by replaying a
 * seed and comparing fingerprints, and a genuinely random sound effect would
 * make two identical runs differ. It advances per call, so a cue varies from
 * firing to firing while the whole session stays reproducible from its seed.
 */
function jitter(game) {
  game.sfxN = ((game.sfxN || 0) * 1664525 + 1013904223) >>> 0;
  return game.sfxN / 4294967296;
}

export function noise(game, dur, from, to, v, q = 1) {
  const ac = game.ac();
  if (!ac) return;
  if (!game.noiseBuf) {
    const n = ac.sampleRate * 1.2;
    game.noiseBuf = ac.createBuffer(1, n, ac.sampleRate);
    const d = game.noiseBuf.getChannelData(0);
    let seed = 12345;
    for (let i = 0; i < n; i++) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      d[i] = (seed / 2147483648) - 1;
    }
  }
  const src = ac.createBufferSource();
  src.buffer = game.noiseBuf;
  src.loop = true;
  const filt = ac.createBiquadFilter();
  filt.type = 'lowpass';
  filt.Q.value = q;
  filt.frequency.setValueAtTime(from, ac.currentTime);
  filt.frequency.exponentialRampToValueAtTime(Math.max(40, to), ac.currentTime + dur);
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(v, ac.currentTime + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
  src.connect(filt); filt.connect(g); g.connect(out(game));
  /* A DIFFERENT SLICE OF THE BUFFER EACH TIME. It loops, so every offset is
   * valid and this costs nothing — and it is the whole difference between a
   * machine repeating one sample and a sound. */
  src.start(ac.currentTime, jitter(game) * 1.0);
  src.stop(ac.currentTime + dur + 0.02);
}

const CUE_RATE: Record<string, number> = { hit: 1.3, hit_heavy: 1.25, kill: 1.3 };   // 'die' left with the hero's 'fall' (round 42)

export function blip(game, f, to, d, type, v, at = 0, detune = 0) {
  const ac = game.ac();
  if (!ac) return;
  const t0 = ac.currentTime + at;
  const o = ac.createOscillator(), g = ac.createGain();
  o.type = type;
  // ...and a pitched cue is never twice at exactly the same pitch. +-35 cents
  // is under a quarter tone: audible as life, not as being out of tune.
  o.detune.value = detune + (jitter(game) - 0.5) * 70;
  o.frequency.setValueAtTime(f, t0);
  o.frequency.exponentialRampToValueAtTime(Math.max(20, to), t0 + d);
  // a 4ms attack: every sound used to start with an audible click
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(v, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + d);
  o.connect(g); g.connect(out(game));
  o.start(t0); o.stop(t0 + d + 0.02);
}

/** A short melodic figure, so events that mean different things sound
 *  different rather than all being one rising triangle sweep. */
export function phrase(game, notes, type = 'triangle', v = 0.05, step = 0.075) {
  notes.forEach((f, i) => game.blip(f, f, step * 1.6, type, v, i * step));
}

/* WHERE THE SOUND IS.
 *
 * Every one of the nineteen cues was mono and dead-centre: no `StereoPanner`
 * anywhere in this file, so a Warden roaring off the left edge and a pod you
 * placed at your own feet arrived at the same point in the stereo field.
 * Nineteen cues carrying zero spatial information, on a game whose screen is
 * 256 pixels wide and whose threats come at you from a direction.
 *
 * `sfx(game, name, x)` takes the emitter's x in screen pixels. Cues with no
 * position — the UI tick, a fanfare, anything the player did rather than
 * something in the world — pass nothing and stay centred, which is correct
 * rather than a fallback.
 *
 * The spread is +-0.6, not +-1. A hard-panned cue vanishes for anyone playing
 * on one earbud, and this is a 256-pixel window: the whole screen is about a
 * head-turn wide, so the pan is a hint about which side, not a claim about
 * where. */
const PAN_SPREAD = 0.6, SCREEN_W = 256;
/** THE TWO LEVELS (LS-300): the effects bus sits at 0.7 and the music bus at 0.9 by design; the sliders scale
 *  those, so 100 is the mix as tuned and 0 is silence, and the toggles keep their own meaning on top. */
export const MUSIC_BUS = 0.9, SFX_BUS = 0.7;
export function applyVolumes(game) {
  const m = Math.max(0, Math.min(1, +game.musicVol)), s = Math.max(0, Math.min(1, +game.sfxVol));
  if (game.musicBus) game.musicBus.gain.value = MUSIC_BUS * (Number.isFinite(m) ? m : 1);
  if (game.bus) game.bus.gain.value = SFX_BUS * (Number.isFinite(s) ? s : 1);
}
function out(game) {
  const ac = game.ac();
  const p = game.sfxPan;
  if (!ac || p == null || !ac.createStereoPanner) return game.bus;
  const pan = ac.createStereoPanner();
  pan.pan.value = Math.max(-1, Math.min(1, p));
  pan.connect(game.bus);
  return pan;
}

/* SAMPLES FIRST, SYNTH SECOND (LS-191). `public/sfx/manifest.json` (written
 * by tools/sfx.ts from ElevenLabs sound generation) names the takes each cue
 * has; the game fetches the manifest once at boot and decodes a take the
 * first time its cue fires. A cue with takes plays one at random, a little
 * detuned, panned like the synth; a cue without falls back to the synthesised
 * one below, so the game never depends on the files being there (the suites
 * run without them). */
const BANK: { files: Record<string, string[]>; peaks: Record<string, number[]>; loud: Record<string, number[]>; top: Record<string, number[]>; buffers: Map<string, any>; loading: Set<string>; ready: boolean } = { files: {}, peaks: {}, loud: {}, top: {}, buffers: new Map(), loading: new Set(), ready: false };
/* WHERE THE SOUND LANDS (LS-191). A take's loudest moment is not its first
 * sample (a whoosh peaks 0.23 s in); the frame the game fires a cue is the
 * frame the action starts. Each cue names how long after its fire the thing
 * you should hear it on happens -- a swing's blade is live from frame 4 to
 * 14, so its middle is 0.13 s -- and a take starts `peak - action` seconds in
 * so its peak lands there. `tools/sfx-peaks.ts` measures the peaks. */
export const CUE_ACTION: Record<string, number> = { swing: 0.13, thrust: 0.1, spin: 0.2, kill: 0.04, dodge: 0.05, blast: 0.02, hit: 0, hit_heavy: 0, clang: 0, chip: 0, crash_stone: 0, crash_wood: 0, crash_brush: 0, riposte: 0, thud: 0, break: 0 };
export function loadSampleBank(base = '/sfx/') {
  if (typeof fetch !== 'function' || BANK.ready) return;
  BANK.ready = true;
  fetch(base + 'manifest.json').then((r) => (r.ok ? r.json() : null)).then((m) => {
    if (!m || typeof m !== 'object') return;
    // either shape: `cue: [files]` or `cue: { files, peak }`
    for (const [cue, entry] of Object.entries<any>(m)) {
      if (Array.isArray(entry)) { BANK.files[cue] = entry; BANK.peaks[cue] = entry.map(() => 0); }
      else if (entry && Array.isArray(entry.files)) { BANK.files[cue] = entry.files; BANK.peaks[cue] = entry.files.map((_, i) => +(entry.peak && entry.peak[i]) || 0); BANK.loud[cue] = entry.files.map((_, i) => (entry.loud && Number.isFinite(+entry.loud[i])) ? +entry.loud[i] : NaN); BANK.top[cue] = entry.files.map((_, i) => (entry.top && Number.isFinite(+entry.top[i])) ? +entry.top[i] : NaN); }
    }
  }).catch(() => { /* no bank: the synth plays */ });
  BANK.files = BANK.files || {};
  (BANK as any).base = base;
}
export function sampleBank() { return BANK; }
/** The suites characterise the synthesised cues; they switch the bank off. */
export function setSampleBankEnabled(on: boolean) { (BANK as any).off = !on; }
/** A decoded take, summed to mono: the generated files are stereo with a
 *  slight lean, and a cue with no position has to sit dead centre. */
function toMono(ac, buf) {
  if (!buf || buf.numberOfChannels === 1 || !ac.createBuffer) return buf;
  const out = ac.createBuffer(1, buf.length, buf.sampleRate);
  const d = out.getChannelData(0);
  for (let c = 0; c < buf.numberOfChannels; c++) { const src = buf.getChannelData(c); for (let i = 0; i < d.length; i++) d[i] += src[i] / buf.numberOfChannels; }
  return out;
}
/** Decode every take of the bank once there is a context to decode with,
 *  two at a time, so a fresh session is not a burst of a hundred fetches. */
function warmBank(ac) {
  if ((BANK as any).warm || !ac || typeof fetch !== 'function') return;
  (BANK as any).warm = true;
  const keys: string[] = [];
  for (const files of Object.values(BANK.files || {})) for (const f of files as string[]) keys.push((BANK as any).base + f);
  let i = 0;
  const next = () => {
    while (i < keys.length && (BANK.buffers.has(keys[i]) || BANK.loading.has(keys[i]))) i++;
    if (i >= keys.length) return;
    const key = keys[i++];
    BANK.loading.add(key);
    fetch(key).then((r) => r.arrayBuffer()).then((ab) => ac.decodeAudioData(ab)).then((b) => { BANK.buffers.set(key, toMono(ac, b)); })
      .catch(() => { /* a take that will not decode is one the synth covers */ })
      .then(() => { BANK.loading.delete(key); next(); });
  };
  next(); next();
}
/** How much of the bank is decoded: for the suites. */
export function bankWarmth() { const n = Object.values(BANK.files || {}).reduce((a, f) => a + (f as string[]).length, 0); return { decoded: BANK.buffers.size, total: n }; }
function playSample(game, name): boolean {
  if ((BANK as any).off) return false;
  const src_cue = ALIAS[name] || name;   // a cue that borrows another's takes borrows its LOUDNESS too (2026-09-17): pickup_sap read an empty row and played 28 dB under
  const takes = BANK.files[src_cue];
  if (!takes || !takes.length) return false;
  const ac = game.ac();
  if (!ac) return false;
  warmBank(ac);
  let ti = Math.floor(Math.random() * takes.length), take = takes[ti];
  let key = (BANK as any).base + take;
  let buf = BANK.buffers.get(key);
  if (!buf) {
    /* NEVER SILENT FOR WANT OF A TAKE. Vijay: "sfx are not always playing".
     * A take that had not been decoded yet handed the fire to the synth, and
     * most cues have no synth case, so the first fires of every cue in a
     * session were silence. Any decoded take of the same cue plays instead;
     * the whole bank is warmed from the first sound in any case. */
    for (let k = 0; k < takes.length; k++) {
      const alt = (BANK as any).base + takes[k], b = BANK.buffers.get(alt);
      if (b) { ti = k; take = takes[k]; key = alt; buf = b; break; }
    }
  }
  if (!buf) {
    if (!BANK.loading.has(key)) {
      BANK.loading.add(key);
      fetch(key).then((r) => r.arrayBuffer()).then((ab) => ac.decodeAudioData(ab)).then((b) => { BANK.buffers.set(key, toMono(ac, b)); BANK.loading.delete(key); }).catch(() => { BANK.loading.delete(key); BANK.files[ALIAS[name] || name] = []; });
    }
    return false;   // the synth covers this one fire; the take is ready for the next
  }
  try {
    const src = ac.createBufferSource();
    src.buffer = buf;
    // ENEMY HITS AND DEATHS SIT HIGHER (LS-209). Vijay: "the enemy damage and
    // death sounds need to be higher pitched": the takes play a minor third up
    src.playbackRate.value = (CUE_RATE[name] || 1) * (0.96 + Math.random() * 0.08);
    const gain = ac.createGain();
    const level = takeGain(name, BANK.loud[src_cue] && BANK.loud[src_cue][ti], BANK.top[src_cue] && BANK.top[src_cue][ti]);   // the target stays `name`'s (its family, its override); the measurement is the take's own
    gain.gain.value = 0;
    src.connect(gain);
    gain.connect(out(game));   // ONE PAN (2026-09-17): out() already returns a fresh panner at a clamped game.sfxPan, so panning here as well sent the sound through two and ran it up to 1.7 dB hot at the edges
    // the peak lands on the action: a late-peaking take starts part-way in, an
    // early-peaking one (swing_2 peaks 0.04 s in) waits the difference
    const peak = (BANK.peaks[src_cue] && BANK.peaks[src_cue][ti]) || 0;   // the peak belongs to the TAKE, as its loudness does (2026-09-18): read under the caller's name it was undefined for an aliased cue, so a borrowed take started at the top of the file instead of on its own peak
    const action = CUE_ACTION[name] ?? 0;
    /* ...AND ALIGNMENT MAY NOT EAT THE TAKE (2026-09-18). A take whose loudest moment is late is started at its peak,
     * which for a SUSTAINED take is most of the way through it: the wisp's voice played 60 ms of its 800, eighteen of
     * the hundred and thirty-three takes played less than half of themselves, and what a player heard was the tail of
     * a sound whose shape was the part that was skipped. A percussive take still gets its alignment -- the swing's
     * peak is 0.23 s against an action at 0.13, and 0.10 fits under the cap -- but nothing starts more than 120 ms
     * in, or more than a third of the way through itself. */
    const align = Math.max(0, peak - action);
    const lead = Math.min(align, 0.12, buf.duration / 3, Math.max(0, buf.duration - 0.05)), wait = Math.max(0, action - peak);
    /* NO CLICK AT EITHER END (LS-197). Vijay: "sword sounds have a click in
     * the beginning". Peak alignment starts a take PART-WAY IN -- a swing take
     * peaks 0.23 s in, so playback begins at 0.23 s, in the middle of a
     * waveform -- and jumping a buffer from silence to a non-zero sample is a
     * step edge: a click, on the most-fired cue in the game. A 6 ms ramp up
     * and a 25 ms ramp down cost nothing audible on a transient and remove
     * both edges (the tail matters too: `src.stop` used to cut the take dead
     * when the next one took its slot). */
    const t0 = ac.currentTime + wait;
    const rate = src.playbackRate.value || 1;
    const span = Math.max(0.06, (buf.duration - lead) / rate);
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(level, t0 + 0.006);
    gain.gain.setValueAtTime(level, t0 + Math.max(0.02, span - 0.025));
    gain.gain.linearRampToValueAtTime(0, t0 + span);
    src.start(t0, lead);
    src.stop(t0 + span + 0.01);
    return true;
  } catch (e) { return false; }
}

/* THE MIX HAS RULES (LS-197). Vijay: "the sound design is over the place
 * currently it's very confusing". It was: forty-three generated takes at
 * whatever loudness the generator gave them, and a killing thrust fired
 * thrust + hit + kill + launch + thud inside one second, the warden roared
 * from twelve sites, and a footstep landed under every blow. Four rules, one
 * place:
 *
 *   1. ONE IMPACT PER MOMENT. Impact cues (a blow, a clang, a kill, a break,
 *      a body landing) queue for the frame and only the highest priority one
 *      plays, at the top of the next update; an impact inside five frames of
 *      a louder one is under it and is not played.
 *   2. SMALL SOUNDS DUCK. For eighteen frames after a real blow, footsteps
 *      and ticks are silent; you hear the hit, not the shuffle.
 *   3. VOICES ARE RATIONED. A creature voice (roar, growl, charge) plays at
 *      most once in thirty frames, and no cue plays twice in three frames.
 *   4. ONE LEVEL PER FAMILY. Every take is measured (tools/sfx-peaks.ts
 *      writes the RMS of its loudest 50 ms in dBFS) and played at its
 *      family's target, so a hit is always a hit's loudness, a step a step's,
 *      whatever the generator did. */
export type Family = 'impact' | 'motion' | 'voice' | 'step' | 'ui' | 'world' | 'loop';
export const FAMILY: Record<string, Family> = {
  hit: 'impact', hit_heavy: 'impact', clang: 'impact', chip: 'impact', crash_stone: 'impact', crash_wood: 'impact', crash_brush: 'impact', riposte: 'impact', kill: 'impact', break: 'impact',
  blast: 'impact', thud: 'impact', hurt: 'impact', flip: 'impact', die: 'impact',
  /* A BODY THROUGH THE AIR IS NOT A BLOW (the music lens, round 50). `launch` is queued on the same frame as the
   * hit that threw the body, and as an impact at priority 2 it lost that frame to the hit every time: fired 122,
   * heard 0, over fifteen minutes of play. As a motion it plays at -19 under the blow's -9, which is what its two
   * takes are -- the whoosh beneath the hit. */
  launch: 'motion',
  swing: 'motion', thrust: 'motion', spin: 'motion', dodge: 'motion',
  roar: 'voice', growl: 'voice', charge: 'voice', wisp: 'voice',
  tick: 'ui', refuse: 'ui', pickup: 'ui', buy: 'ui', fanfare: 'ui', toll: 'ui', reveal: 'ui', stone: 'ui', eat: 'ui', bag: 'ui', sap: 'ui', wield: 'ui', pickup_sap: 'ui',
  crush: 'impact', choke: 'voice', bleed: 'world', poison: 'world', fall: 'impact',   // the pivot's verbs (LS-233): each had borrowed a UI cue or had none; `poison` joined them in round 50, the last affliction that hurt you in silence
  grind: 'world', drop: 'world', splash: 'world', flame: 'world', burn: 'world', brazier: 'world', thunder: 'world', rumble: 'world', lap: 'world', creak: 'world', wake: 'world',   // the hull under way (LS-390)
  unseal: 'world', enter: 'world', ebb: 'world',
};
export const PRIORITY: Record<string, number> = {
  // clang above hit: the riposte's clang lands on the same frame as the hit it
  // rides, and a clang that loses to a plain hit is a riposte nobody heard
  kill: 6, die: 6, fall: 7, riposte: 6, hit_heavy: 5, break: 5, blast: 5, hurt: 6, clang: 5, chip: 5,   /* hurt: 6 (2026-09-20) -- level with a kill: an impact ties and LOSES inside the five-frame shadow, so a bite two frames after your own axe chipped a trunk was heard as nothing */ crush: 5, poison: 3, crash_stone: 4, crash_wood: 4, crash_brush: 4, hit: 4, flip: 3, thud: 3, launch: 2,
};
/** dBFS (RMS of the loudest 50 ms) each family is played at. */
/* Footsteps sit twenty decibels under a blow. Vijay: "footsteps are way too
 * loud" -- levelling the bank had lifted takes recorded at -46 dBFS up to a
 * step target of -27, which put a shuffle inside a fight. A step is a texture
 * you notice when nothing else is happening. */
export const TARGET_DB: Record<Family, number> = { impact: -13, voice: -15, motion: -19, world: -19, ui: -21, step: -40, loop: -28 };
/* ...AND SOME CUES SIT ABOVE THEIR FAMILY. Vijay, after the first levelling:
 * "footsteps are still too loud, enemy damage and defeat sounds need to be
 * louder". Steps went down another seven decibels (they are texture, heard
 * when nothing else is happening). The blows a creature takes and the sound
 * of it going down are the reward for the swing, and they carry the fight:
 * they sit four to six decibels above the rest of the impacts, so a hit is
 * never buried under a clang, and a kill is the loudest thing in the mix. */
export const CUE_DB: Record<string, number> = { lap: -20, creak: -17, wake: -22, hit: -9, hit_heavy: -8, kill: -7, fanfare: -11, toll: -7, stone: -9, thunder: -6, rumble: -22 }   // the strike is the loudest thing in the world; the rumble before it is far off (LS-381)   // the win was a menu tick's -21, fourteen under a kill (the music lens, round 48);
/** The level a cue plays at: its own override, else its family's. */
export function targetOf(name: string): number {
  return CUE_DB[name] !== undefined ? CUE_DB[name] : TARGET_DB[familyOf(name)];
}
export function familyOf(name: string): Family {
  if (name.startsWith('step_')) return 'step';
  if (name.startsWith('amb_')) return 'loop';
  return FAMILY[name] || 'world';
}
/** The gain that puts a take of measured loudness `loud` at its family's
 *  target; a take with no measurement plays at a plain family level. */
export const CEILING_DB = -0.5;   // no take leaves the bus above this (round 53's music lens: kill_0 lifted to its target peaked at +0.4 dBFS raw, hit at -0.1)
export function takeGain(name: string, loud: number | undefined, top?: number): number {
  const fam = familyOf(name);
  const plain = { impact: 0.7, voice: 0.6, motion: 0.5, world: 0.5, ui: 0.45, step: 0.12, loop: 0.3 }[fam];
  if (loud === undefined || !Number.isFinite(loud)) return plain;
  const target = targetOf(name);
  /* THE CREST IS KEPT UNDER THE CEILING: a take's loudness is the RMS of its loudest 50 ms, and a hit's single
   * loudest sample sits 7-14 dB above that, so a lift that lands the RMS on target can put the crest over 0 dBFS.
   * With the take's top sample measured (tools/sfx-peaks.ts `top`), the lift stops where the crest meets the ceiling. */
  const crest = (top !== undefined && Number.isFinite(top)) ? Math.pow(10, (CEILING_DB - top) / 20) : Infinity;
  return Math.min(crest, Math.max(0.05, Math.min(16, Math.pow(10, (target - loud) / 20))));
  /* The cap is wide on purpose. The generator handed back takes from -8 to
   * -46 dBFS -- a footstep recorded 30 dB under a hit -- so bringing the bank
   * to one level is a +24 dB lift at the quiet end and a -6 dB cut at the
   * loud. A take that still cannot reach its family's level is a dud take,
   * and the soak names it rather than letting it play quiet. */
}
const QUIET_FRAMES = 18, IMPACT_SHADOW = 5, VOICE_GAP = 30, SAME_GAP = 3;
type Mix = { queue: { name: string; x?: number; prio: number }[]; quietUntil: number; lastVoice: number; lastImpactAt: number; lastImpactPrio: number; held: number; last: Map<string, number>; log: [number, string][] };
function mixOf(game): Mix {
  return game.mix || (game.mix = { queue: [], quietUntil: -1, lastVoice: -999, lastImpactAt: -999, lastImpactPrio: 0, held: 0, last: new Map(), log: [] });
}
function logged(game, m: Mix, name: string) {
  m.log.push([game.frame | 0, name]);
  const cap = game.mixLogMax || 64;
  while (m.log.length > cap) m.log.shift();
}
/** Every cue the game fires comes through here. Impacts wait for the flush;
 *  everything else plays now or is dropped by the rules above. */
/** Cues that borrow another's takes: the sap pickup plays the sap takes -- the generator gave two near-silent files for a pop of its own (round 43). */
export const ALIAS: Record<string, string> = { pickup_sap: 'sap' };
export function cue(game, name: string, x?: number) {
  const m = mixOf(game), f = (game.frame | 0) + m.held, fam = familyOf(name);   // the mixer's own clock: `game.frame` stops while the bag is open (the music lens, round 50)
  if (fam === 'impact') { m.queue.push({ name, x, prio: PRIORITY[name] ?? 1 }); return undefined; }
  if ((fam === 'step' || name === 'tick') && f < m.quietUntil) return undefined;
  if (fam === 'voice') { if (name !== 'roar' && name !== 'charge' && f - m.lastVoice < VOICE_GAP) return undefined; m.lastVoice = f; }   // a beast's roar and a charge's snort are one-shot warnings: they win the ration rather than lose to a bleat (2026-09-19)
  if (f - (m.last.get(name) ?? -999) < SAME_GAP) return undefined;
  m.last.set(name, f);
  logged(game, m, name);
  return sfx(game, name, x);
}
/** The one impact of the frame. Called at the top of `update()`. */
export function flushCues(game) {
  const m = game.mix as Mix | undefined;
  if (!m || !m.queue.length) return;
  let best = m.queue[0];
  for (const q of m.queue) if (q.prio > best.prio) best = q;
  m.queue.length = 0;
  const f = (game.frame | 0) + m.held;
  if (f - m.lastImpactAt < IMPACT_SHADOW && best.prio <= m.lastImpactPrio) return;
  m.lastImpactAt = f; m.lastImpactPrio = best.prio;
  if (best.prio >= 4) m.quietUntil = f + QUIET_FRAMES;
  logged(game, m, best.name);
  sfx(game, best.name, best.x);
}

export function sfx(game, name, x?: number) {
  /* THE X IS A WORLD PIXEL, AND THE PAN IS ABOUT THE WINDOW (2026-09-18). The comment above this function has said
   * "screen pixels" since LS-197, and almost every caller in the game passes a world one -- `e.x + 8`, `b.x`,
   * `tx * TS + this.originX()`. The wilds are one coordinate space thousands of pixels wide, so `x - 128` was
   * always far past the half-screen and every positioned sound in the game clamped to the right-hand limit:
   * measured over sixteen screens of play, 1,081 of 1,081 positioned cues panned +0.600, none of them anywhere
   * left of centre. The window's origin comes off first now, which is the number the formula was written for. */
  const cam = typeof x === 'number' && game.camera ? game.camera() : null;
  const wx = cam ? x! - cam.x : x;
  game.sfxPan = typeof wx === 'number' && Number.isFinite(wx)
    ? Math.max(-PAN_SPREAD, Math.min(PAN_SPREAD,
      ((wx - SCREEN_W / 2) / (SCREEN_W / 2)) * PAN_SPREAD))
    : null;
  try { if (game.sfxOff) return undefined; if (!game.muted && playSample(game, name)) return undefined; return sfxAt(game, name); } finally { game.sfxPan = null; }
}

function sfxAt(game, name) {
  switch (name) {
    case 'swing': return game.noise(0.09, 5200, 900, 0.05, 0.7);
    case 'hit': {
      // THE MOST IMPORTANT SOUND IN THE GAME WAS THE QUIETEST THING IN IT.
      // A 90ms lowpass sweep from 1400 to 220 at Q 2 puts almost no energy
      // in the band the ear weights, so a landed blow measured below the
      // swing that precedes it and below the clang of one that was refused.
      // Wider, louder, and with a low thump under it: a hit has a body.
      game.blip(260, 120, 0.08, 'square', 0.055);
      return game.noise(0.1, 2600, 300, 0.12, 1);
    }
    case 'clang': {                       // two detuned squares: metal, not a beep
      game.blip(1180, 900, 0.1, 'square', 0.04, 0, 0);
      game.blip(1180, 900, 0.1, 'square', 0.04, 0, 52);
      return game.noise(0.05, 6000, 2000, 0.03, 1);
    }
    case 'hurt':
      game.blip(300, 90, 0.2, 'square', 0.05);
      return game.noise(0.16, 1800, 200, 0.05, 1);
    case 'blast':                         // broadband, with a body thump
      game.noise(0.5, 4200, 120, 0.12, 0.6);
      return game.blip(120, 34, 0.34, 'sine', 0.09);
    case 'grind': return game.noise(0.3, 900, 260, 0.06, 3);
    // THE ACCEPT CUE, which was written and never given a sound. `main.js`
    // calls `sfx('tick')` on every accepted press — the comment above that
    // call says it is the whole confirmation on iOS, because Safari has no
    // Vibration API — and this switch had no case for it, so it fell to
    // `default: return undefined`. Measured by instrumenting the audio
    // primitives: `sfx('tick')` fired 0 of them, `sfx('enter')` fired 3. An
    // accepted press was the QUIETER of the two outcomes on a phone; the
    // refusal had a 4.5:1 ring and the acceptance had nothing.
    //
    // Short, high and quiet: a confirmation is not an event, and it will be
    // heard hundreds of times a run.
    case 'tick': return game.blip(1180, 900, 0.035, 'triangle', 0.022);
    case 'drop': return game.blip(300, 150, 0.09, 'square', 0.04);
    /* THE REFUSE CUE, and it is its own sound.
     *
     * A refused press had exactly one channel: 110ms of red on the glass.
     * `navigator.vibrate` is Android-only and was not called on this path at
     * all, so on iOS a refusal was a flash and silence -- and the flash is
     * under the thumb on the five mini controls, where 57% of the ring falls
     * inside an 11mm contact patch. The most-refused control in the game is
     * POD with no pods, in a fight.
     *
     * It does not borrow `enter` or `tick`. This file's own history is two of
     * three tools sharing another verb's sound, and an accept cue that was
     * called for a year and had no `case`, so it fell to `default` and played
     * nothing while the comment above the call said it was the whole
     * confirmation on iOS.
     *
     * F4 -> E4, falling. Both are in A aeolian, which is the key of every
     * track that can be playing when a press is refused, and the falling
     * semitone is the hollow's own phrygian cadence -- the sound this score
     * already uses for a thing closing. Quiet and 90ms, because it answers a
     * mistake and will be heard often.
     */
    case 'refuse': return game.phrase([349, 330], 'square', 0.04, 0.045);
    // A CUE OF ITS OWN. The lung's jet was going to borrow `enter`, which
    // already covers six events including a menu confirm — the exact
    // "two of three tools borrow another verb's sound" finding. Water is
    // filtered noise with a falling edge, and nothing else in the game is.
    /* 0.16, NOT 0.05. `splash` is the Silt Lung's exhale — one of the three
     * charms the game is built on — and at 0.05 it rendered at -36.8 dBFS,
     * the QUIETEST cue in the game, 17.4 dB under `blast`. That is numerically
     * the same fault this file has already had twice: `flame` was raised from
     * -37.7 and `growl` from -26.5 on the argument that a charm you cast must
     * be heard. The third charm was never measured, because `water.ts` renders
     * eight of the nineteen cues by name and this was not one of them. */
    case 'splash': return game.noise(0.22, 1800, 380, 0.16, 2);
    /* THE HULL UNDER WAY (LS-390): water lapping the planks, a creak on a turn, the wake's hiss at speed; the takes
     * in public/sfx carry these, and these are what plays without them */
    case 'lap': return game.noise(0.3, 900, 300, 0.05, 1);
    case 'creak': return game.blip(180, 90, 0.12, 'sawtooth', 0.03);
    case 'wake': return game.noise(0.4, 2400, 900, 0.04, 1);
    case 'die':
      game.blip(400, 50, 0.8, 'square', 0.06);
      return game.noise(0.7, 1600, 90, 0.05, 1);
    /* A TELEGRAPH YOU CANNOT HEAR IS NOT A TELEGRAPH.
     *
     * Rendered through the real node graph into an OfflineAudioContext (see
     * `test/water.ts`), `growl` measured -36.5 dBFS peak against `blast` at
     * -19.4 — seventeen decibels under, and it is the sound that says an
     * enemy is about to commit. 0.20 puts it at -26.0, between `hurt` and
     * `roar`: present, and still not the loudest thing in a fight.
     *
     * The Q stays at 6. Widening it to 1.5 moves the peak by 1.5 dB and
     * changes the CHARACTER — the narrow band is what makes it a growl rather
     * than a hiss — so the level was the fault and the level is what moved. */
    case 'growl': return game.noise(0.3, 420, 140, 0.20, 6);
    case 'roar':
      game.blip(96, 46, 0.55, 'sawtooth', 0.06);
      return game.noise(0.5, 700, 120, 0.06, 3);
    // each of these used to be the same rising triangle
    case 'pickup': return game.phrase([880, 1320], 'triangle', 0.05, 0.055);
    case 'pickup_sap': return game.phrase([330, 262], 'sine', 0.07, 0.07);   // a low soft pop: sap, not glim
    case 'buy': return game.phrase([660, 990, 1320], 'square', 0.04, 0.07);
    /* THE EMBERWAKE WAS THE QUIETEST SOUND IN THE GAME, TWICE.
     *
     * -37.7 dBFS peak, last of eleven cues, 18.3 dB under `blast` — one of the
     * three charms the whole game is built around, and it whispered. It was
     * raised to 0.16 and this comment recorded "-27.6, just under `hurt`",
     * which was true and measured on the wrong instrument. On a peak meter a
     * broadband noise burst reads loud; in the ear it does not. On loudness,
     * 0.16 measured -46.3 — sixteenth of nineteen and 6.6 dB under `hit`,
     * where peak claimed 4.7. The fix under-delivered by three decibels and
     * the census agreed with it for three rounds.
     *
     * 0.28 puts it at -41.4, alongside `hurt` at -41.7 and `clang` at -41.6.
     * Not higher: 0.34 measures -39.7, which is louder than a landed blow, and
     * `hit` is the sound this game most needs a player to notice. */
    case 'thunder': return game.noise(1.4, 220, 36, 0.55, 2);   // the strike (LS-381): a long low crack that falls into a rumble
    case 'flame': return game.noise(0.28, 900, 2600, 0.28, 0.8);
    case 'unseal': return game.phrase([740, 554, 740, 1108], 'triangle', 0.05, 0.09);
    case 'reveal': return game.phrase([523, 659, 784, 1046], 'triangle', 0.05, 0.08);
    /* POISON, WHICH HAD NO SOUND AT ALL (the music lens, round 50: the only affliction that takes a point off you in
     * silence, while smoke coughs, a tusk wound bleeds and scorched ground burns). It is a spore in the blood, so:
     * a low soft pair falling a minor third, under the voices and over the beds. There is no take for it in the bank
     * and it does not need one -- the synth is the sound, the way `refuse` and `enter` are. */
    case 'poison': return game.phrase([196, 165], 'sine', 0.06, 0.11);
    case 'enter': return game.phrase([392, 294], 'sine', 0.05, 0.1);
    case 'fanfare': return game.phrase([523, 659, 784, 1046, 1318], 'triangle', 0.055, 0.11);
    /* A GREAT BEAST FALLING IS NOT A PICKUP (the music lens, round 51). Felling one of the three was `fanfare` --
     * the same cue as taking the emberwake off the ground -- so the whole shape of the run, three kills and no
     * more, was marked by the sound of finding a charm. Open fifths in C, no third: consonant against the wilds
     * (C), the hollow (bVII) and the boss (VI), so it lands whatever is playing under it, and slow enough to be a
     * toll rather than a flourish. */
    case 'toll': return game.phrase([196, 262, 392, 523, 784], 'triangle', 0.14, 0.5);
    case 'stone': {   // the way out (round 52's music lens): open fifths settling downward, the run set down
      /* ...ON THE PIANO THAT IS PLAYING (round 53's music lens): a cue with no take fell to the triangle phrase, the
       * chiptune voice this score removed, fourteen dB under the fanfare it replaced. The room's own lead patch
       * strikes the four notes, so the ending is in the score's voice; the phrase stays for a page with no music. */
      const j = game.jukebox;
      if (j && j.track && j.ac && typeof j.strike === 'function') { const t = j.ac.currentTime + 0.02; [67, 60, 55, 48].forEach((m, i) => j.strike('lead', m, t + i * 0.22, 1.4, 0.8, game.bus || undefined)); return; }   // ...on the EFFECTS bus (2026-09-19): it is a sound effect, and the music slider silenced it   // STRUCK LOOSE OF THE TRACK (2026-09-18): this hung the four notes on the current track's output gain, and the same call sets mode='win', which fades that gain out eight frames later -- 84% of the cue never sounded
      return game.phrase([392, 262, 196, 131], 'triangle', 0.12, 0.9);
    }
    // A CUE THE SYNTH DOES NOT KNOW STILL MAKES A SOUND: a short soft tap,
    // for the one fire before its take has decoded. Vijay: "sfx are not
    // always playing" -- twenty of the bank's cues had no case here, and
    // every first fire of each was nothing.
    default: return game.noise(0.05, 2400, 500, 0.03, 1);
  }
}

/** Pick the track the moment deserves and let the player run. The music
 *  itself lives in music-data.js, written in rondocode; see audio/README.md.
 *  Death is silent on purpose — the only time the game stops singing. */
/** The lowpass the score sits under, from life left: 900 Hz at a quarter or less, open above. Pure, so a check can read it. */
/* THE DURESS IS A SLOPE, NOT A CLIFF (the music lens, round 48: it measured 12000 Hz at three eighths of life and 900
 * at two, so the score went from open to muffled in one heart). Open above half, closing steadily from there, and shut
 * at a quarter and below. The title is still never muffled by the run it left. */
export function duressHz(hp, maxHp, mode = 'play', under = false) {
  /* UNDER THE WATER, THE SCORE IS HEARD THROUGH IT (the music lens, round 49). Submerging is a state the world grew
   * with the water volume and the mixer had no word for; the lowpass already on the music bus is the whole fix. */
  const dip = (hz: number) => (under && (mode === 'play' || mode === 'dying') ? Math.min(hz, 700) : hz);
  if (!(mode === 'play' || mode === 'dying') || !(maxHp > 0)) return 12000;
  const life = hp / maxHp;
  if (life > 0.5) return dip(12000);
  if (life <= 0.25) return dip(900);
  return dip(Math.round(12000 * Math.pow(900 / 12000, (0.5 - life) / 0.25)));
}
export function music(game) {
  /* THE MIXER KEEPS ITS OWN TIME (the music lens, round 50). `update()` returns before `frame++` while the bag is
   * open ("the bag screen holds the world still"), and the mixer's whole clock is `game.frame` -- so every cue a bag
   * session fires carries ONE frame number and the same-cue gap swallows the rest. Measured: five presses two
   * seconds apart inside the bag were heard as two, and closing the bag was silent every time, because `bag` and
   * `tick` on the close sat inside the gap behind the open, however many seconds ago that was. The bag is the one
   * screen that is nothing but presses. `held` is zero whenever the world is moving, so every check that advances
   * `game.frame` by hand still means what it meant. */
  if (game.bagOpen) mixOf(game).held++;
  { const b = game.enemies && game.inWindow ? game.enemies.find((e) => e && !e.dead && e.def && e.def.beast && game.inWindow(e)) : null; game.kitPulse = b ? kitPulse(b.hp, b.maxHp, !game.beastRoused || game.beastRoused(b)) : 0.35; if (game.jukebox && game.jukebox.setPulse) game.jukebox.setPulse(game.kitPulse); }
  if (game.musicDuress && game.state && game.audio) { const hz = duressHz(game.state.hp, game.state.maxHp, game.mode, !!(game.player && (game.player.submerged || game.player.swimming))); if (hz !== game.musicDuressHz) { game.musicDuress.frequency.setTargetAtTime(hz, game.audio.currentTime, 0.4); game.musicDuressHz = hz; } }
  if (game.muted) {
    if (game.jukebox) game.jukebox.stop();
    // ...AND THE BED, WHICH THIS USED TO SKIP PAST. `ambience()` opens with
    // `if (game.muted) return game.hushAmbience()` — a branch this early
    // return made unreachable, so the comment beside it documented a
    // measured fix for a path the call graph had already closed. It looked
    // harmless because `applyMute` zeroes the whole bus, i.e. for a reason
    // that comment does not mention; setting `game.muted` directly left the
    // bed playing at full level.
    try { game.hushAmbience(); } catch (err) { /* no audio on this device */ }
    return;
  }
  // MUSIC OFF, SOUND ON (LS-191): the tune stops, the world keeps its sounds
  if (game.musicOff) {
    if (game.jukebox) game.jukebox.stop();
    try { if (game.sfxOff) game.hushAmbience(); else game.ambience(); } catch (err) { /* no audio on this device */ }
    return;
  }
  const ac = game.ac();
  if (!ac) return;
  if (!game.jukebox) game.jukebox = new Music(ac, game.musicBus);
  // A change of track costs a crossfade, so a request has to hold for a few
  // frames to earn one. A boss room is briefly empty on the frame the fight
  // starts, and a screen transition briefly has no room; without this the
  // music restarted from silence every time either happened.
  // what the place sounds like, under whatever is playing
  try { game.ambience(); } catch (err) { /* no audio on this device */ }
  const want = game.trackFor();
  if (want !== game.wantTrack) { game.wantTrack = want; game.wantFor = 0; }
  else if (game.wantFor < TRACK_HOLD) game.wantFor++;
  /* ...AND WHERE IT IS HEARD. The night's piece is the hollow's piece, whose
   * room is a cave; under an open sky it takes the wilds' room instead. */
  if (game.wantFor >= TRACK_HOLD) game.jukebox.play(want, true);
  game.jukebox.update();
}

export function trackFor(game) {
  if (game.mode === 'dying' || game.mode === 'gameover') return null;
  if (game.mode === 'title') return 'title';
  if (game.mode === 'win') return 'hymn';
  // a Great Beast on the glass is the boss track (LS-233): the piece and its kit sat unplayed since the hollows went
  /* ...AND IT HOLDS. A beast that paces the window's edge flipped the score three times in a second (the music
   * lens, 2026-09-05): the boss piece stays for BOSS_HOLD frames after the beast was last on the glass. */
  if (game.enemies && game.inWindow && game.enemies.some((e) => e && !e.dead && e.def && e.def.beast && game.inWindow(e) && (!game.beastRoused || game.beastRoused(e)))) game.bossUntil = (game.frame || 0) + BOSS_HOLD;   /* roused, not merely seen (round 52's music lens): a sleeping beast on the glass flipped the piece before the kit's rousing signal */
  if (game.bossUntil !== undefined && (game.frame || 0) < game.bossUntil) return 'boss';
  // the night has the second tune (LS-233): the piece written for the hollows, sparse and wet, is the wilds after dark now
  if (game.systems && game.systems.clock && game.systems.clock(game).night) return 'hollow';
  return 'wilds';
}

/* AMBIENCE: WHAT THE PLACE SOUNDS LIKE, under whatever is playing.
 *
 * There was none. One 64-second loop covered all six biomes, day and night,
 * and nothing else made a sound unless you hit it — no wind in the heath,
 * no water at a lakeside, no difference at all between standing in a
 * meadow and standing in an ash field. `trackFor()` cannot fix that: five
 * composed tracks are not six biomes times two times of day, and writing
 * six more is not a tuning change.
 *
 * What a place sounds like is mostly noise, though, and noise is cheap: one
 * filtered bed whose colour and level follow the biome, plus a second voice
 * that rises with how much open water is on the screen. It runs under the
 * music, at a level the mix will not notice, and it stops dead when muted.
 */
/* THE AMBIENCE IS A BED OF LOOPS (LS-191). With the bank present, the place
 * and the hour choose a loop -- wind over the heath by day, crickets by
 * night, the cave's drip in a hollow -- and water and fire nearby add their
 * own layer; each loop's gain ramps toward its target every frame, so a
 * change of screen crossfades. Without the bank the synthesised bed plays. */
const LOOP_GAIN = { amb_wind: 0.32, amb_night: 0.30, amb_cave: 0.36, amb_water: 0.22, amb_fire: 0.26, amb_rain: 0.34 };
/** Each country's bed under the day's loop: the dust is all wind, the scree has the cave under it, the ash a fire far off, the fen its water. Weights times LOOP_GAIN stay at or under 0.5. */
export const BEDS: Record<string, Record<string, number>> = { heath: { amb_wind: 1 }, bloom: { amb_wind: 1, amb_water: 0.12 }   /* a wet meadow: the day's wind cannot be lowered below its base, so the bloom is told apart by a little water */, fen: { amb_water: 0.35 }, dust: { amb_wind: 1.4 }, scree: { amb_wind: 1.2, amb_cave: 0.15 }, ash: { amb_wind: 0.7, amb_fire: 0.2 } };
/* The boss kit's weight from the beast's life: a third at full, whole from half down (the music lens, rounds 45 and
 * 46). Pure.
 *
 * ...AND NOTHING AT ALL WHILE IT SLEEPS (the music lens, round 51). A Great Beast asleep at the edge of the window
 * got the same drums as one mid-charge, because this reads only its LIFE -- so the one moment the score has to mark,
 * a beast noticing you, sounded exactly like the minute before it. The game already knows the difference and says
 * so in one place: `beastRoused`, which is the rule the pack, the leash and the brief all use. */
export function kitPulse(hp, maxHp, awake = true) { if (!awake) return 0; const worn = maxHp > 0 ? Math.max(0, 1 - hp / maxHp) : 0; return Math.min(1, 0.35 + 0.65 * Math.min(1, worn / 0.5)); }
/** What the place wants to sound like: the loops and their weights. Pure, so a check can ask. */
export function ambienceWants(game) {
  const want = new Map();
  const night = game.night || (game.systems && game.systems.clock && game.systems.clock(game).night);
  want.set(night ? 'amb_night' : 'amb_wind', 1);
  let wet = 0;
  if (game.tiles) for (const row of game.tiles) for (const c of row) if (c === T.WATER || c === T.SEA) wet++;
  if (wet > 14) want.set('amb_water', Math.min(1, wet / 60));
  if (game.systems && game.systems.fires && game.systems.fires.length) want.set('amb_fire', Math.min(1, game.systems.fires.length / 6));
  // the fen at night has the cave's drip under it: a shipped loop no place had chosen (the music lens, round 44)
  const biome = game.world && game.world.biome && game.area && game.world.biome[game.area.y] ? game.world.biome[game.area.y][game.area.x] : '';
  if (night && biome === 'fen') want.set('amb_cave', 0.5);
  // six biomes, six beds (the music lens, round 46): the wind's weight and a second loop are the country's, taken as the greater of the two wants
  // ...the wind's weight REPLACES the day's base (so the ash's 0.7 takes) and is not laid under the night's crickets (the music lens, round 47: every country at night had the day's wind under it, the dust louder than by day); the other loops are still the greater of the two wants
  /* THE GROUND THAT CAN KILL YOU IS AUDIBLE (the music lens, round 48: the temperature field is the newest thing that
   * takes a point off you and it made no sound at all). Where a screen is mostly over the scorch line the fire loop
   * comes up under everything; where it is mostly frosted, the water's lap is halved -- cold water is quiet. */
  { const f = game.systems && game.systems.fieldsFor ? game.systems.fieldsFor(game) : null;
    if (f && f.temp && game.player) {
      /* a screen's worth of ground round the ear (LS-511: the field is the world's now, not the screen's) */
      let hot = 0, cold = 0; const x0 = Math.floor(game.player.cx / TS) - (COLS >> 1), y0 = Math.floor(game.player.cy / TS) - (ROWS >> 1);
      for (let y = y0; y < y0 + ROWS; y++) for (let x = x0; x < x0 + COLS; x++) { const t = f.tempAt(x, y); if (t > SCORCH_LINE) hot++; else if (t < FROST_LINE) cold++; }
      if (hot > 8) want.set('amb_fire', Math.max(want.get('amb_fire') || 0, Math.min(1, (hot / (COLS * ROWS)) * 1.5)));
      if (cold > 8 && want.has('amb_water')) want.set('amb_water', want.get('amb_water')! * 0.5);
    } }
  const bed = BEDS[biome]; if (bed) for (const [k, v] of Object.entries(bed)) { if (k === 'amb_wind') { if (!night) want.set('amb_wind', v as number); } else want.set(k, Math.max(want.get(k) || 0, v as number)); }
  /* AND THE RAIN IS AUDIBLE (the music lens, round 51). It rains a third of a run and pours a tenth of it -- it is
   * drawn on the screen and it puts your torch out -- and the score answered with nothing: `LOOP_GAIN` had wind,
   * night, cave, water and fire and no rain at all, so the loudest weather in the game was the only silent one.
   *
   * It comes in over the bed rather than beside it, and DUCKS THE WIND as it does: rain and a dry gust together is
   * two weathers at once, and the wind is the thing rain replaces. Below 0.15 nothing happens -- a shower that is
   * barely drawn should not be heard -- and it reaches full weight at the downpour the sky actually produces. */
  { const r = game.systems && game.systems.rain ? game.systems.rain(game) : 0;
    if (r > 0.15) {
      want.set('amb_rain', Math.min(1, (r - 0.15) / 0.45));
      /* ...AND IT DUCKS WHICHEVER DRY BED IS PLAYING (the music lens, round 52). The first cut ducked `amb_wind`
       * alone -- and at night there IS no wind, the bed is `amb_night`, so a downpour at midnight came out as
       * crickets at full weight with rain over them. Both are the dry weather rain replaces. */
      for (const dry of ['amb_wind', 'amb_night']) {
        const w = want.get(dry);
        if (w) want.set(dry, w * (1 - 0.5 * Math.min(1, r)));
      }
    } }
  return want;
}
/** What a bed plays at: its weight, its country's share, and the levelling its measured take needs to reach the loop
 *  family. Pure, so a check can ask.
 *
 *  LOOP_GAIN WAS APPLIED TO AN UNLEVELLED TAKE (the music lens, round 50). Every cue in the game plays through
 *  `takeGain` so it lands on its family's target; the five beds did not, and `TARGET_DB.loop` was read by nothing.
 *  Measured: the wind take is -53.7 dBFS and the water -27.9, so the bed under 71% of the game's screens played at
 *  -63.6 dBFS against the water's -41.1 -- 22.5 dB apart, which is why a screen with a pond was a stream and every
 *  other screen was silence. LOOP_GAIN is a WEIGHT now, not a level; levelled, the five land inside 4.3 dB. */
export function bedGain(name: string, want: number, loud?: number): number {
  const lvl = Number.isFinite(loud as number) ? takeGain(name, loud as number) : 1;
  return want * (LOOP_GAIN[name] || 0) * lvl;
}
function ambienceLoops(game, ac) {
  const loops = game.ambLoops || (game.ambLoops = new Map());
  const want = ambienceWants(game);
  for (const name of Object.keys(LOOP_GAIN)) {
    const target = bedGain(name, want.get(name) || 0, BANK.loud[name] && BANK.loud[name][0]);
    let L = loops.get(name);
    if (!L) {
      if (!target) continue;
      const takes = BANK.files[ALIAS[name] || name];
      if (!takes || !takes.length) continue;
      const key = (BANK as any).base + takes[0];
      const buf = BANK.buffers.get(key);
      if (!buf) {
        if (!BANK.loading.has(key)) {
          BANK.loading.add(key);
          fetch(key).then((r) => r.arrayBuffer()).then((ab) => ac.decodeAudioData(ab)).then((b) => { BANK.buffers.set(key, b); BANK.loading.delete(key); }).catch(() => { BANK.loading.delete(key); BANK.files[ALIAS[name] || name] = []; });
        }
        continue;
      }
      const src = ac.createBufferSource();
      src.buffer = buf; src.loop = true;
      const gain = ac.createGain(); gain.gain.value = 0;
      src.connect(gain); gain.connect(game.bus);
      try { src.start(); } catch (e) { continue; }
      L = { src, gain }; loops.set(name, L);
    }
    game.rampTo(L.gain.gain, target);
  }
}
export function hasAmbienceBank() { return !(BANK as any).off && Object.keys(LOOP_GAIN).some((n) => BANK.files[n] && BANK.files[n].length); }

export function ambience(game) {
  /* UNDERGROUND HAS A SOUND NOW, because it had none.
   *
   * This read `game.area.kind !== 'wild'`, so every hollow and every cave in
   * the game hushed the bed entirely: the wilds have wind and water and the
   * places you spend the second half of a run in are silent.
   *
   * Worth recording precisely, because a review got it wrong in a way this
   * project has a rule about. An art lens reported that all four underground
   * areas "play the heath meadow's bed", measured off `ambienceFor(palKey)`
   * returning `freq 620, gain 0.030` for `h0`/`h1`/`h2`/`cave`. That is true
   * of the TABLE and false of the GAME — the table was never consulted down
   * there, because this line hushed the graph one call earlier. Measuring the
   * rule and naming the wiring is the same fault as measuring a pixel and
   * prescribing against the palette, which is the lesson that lens carries in
   * `docs/REVIEWERS.md`. Both halves needed fixing; only one of them was the
   * one that was reported. */
  if (game.muted || game.sfxOff) return game.hushAmbience();
  const ac = game.ac();
  if (!ac) return undefined;
  if (hasAmbienceBank()) { ambienceLoops(game, ac); if (game.amb) { game.rampTo(game.amb.bedGain.gain, 0); game.rampTo(game.amb.wetGain.gain, 0); } return undefined; }
  if (!game.amb) {
    // one looping noise source, shaped twice: a wide bed and a bright
    // trickle for water. Built once and left running; only the gains move.
    const secs = 3;
    const buf = ac.createBuffer(1, ac.sampleRate * secs, ac.sampleRate);
    const d = buf.getChannelData(0);
    // ...SEEDED, NOT `Math.random`. Every other generated thing in this
    // project is reproducible from the seed and this has no reason not to
    // be; a determinism check that has to special-case the audio buffer is
    // a check with a hole in it.
    const rng = new RNG(0x5eed1e);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      // brown-ish: integrated white, which is what wind and water are
      last = (last + (rng.next() * 2 - 1) * 0.05) * 0.996;
      d[i] = Math.max(-1, Math.min(1, last * 3));
    }
    const src = ac.createBufferSource();
    src.buffer = buf; src.loop = true;
    const bed = ac.createBiquadFilter();
    bed.type = 'lowpass'; bed.frequency.value = 700; bed.Q.value = 0.6;
    const wet = ac.createBiquadFilter();
    wet.type = 'bandpass'; wet.frequency.value = 2400; wet.Q.value = 0.8;
    const bedGain = ac.createGain(); bedGain.gain.value = 0;
    const wetGain = ac.createGain(); wetGain.gain.value = 0;
    src.connect(bed); bed.connect(bedGain); bedGain.connect(game.bus);
    src.connect(wet); wet.connect(wetGain); wetGain.connect(game.bus);
    try { src.start(); } catch (err) { return undefined; }
    game.amb = { src, bed, wet, bedGain, wetGain };
  }
  const a = game.amb;
  let wet = 0;
  if (game.tiles) {
    for (const row of game.tiles) for (const c of row) if (c === T.WATER) wet++;
  }
  // THE DECISION IS A PURE FUNCTION, and this is only the wiring. Buried in
  // the node graph it could not be checked without an AudioContext, and
  // there is none in node — which is how `labels.js` ended up existing too.
  const want = ambienceFor(game.palKey, game.night, wet);
  game.rampTo(a.bed.frequency, want.freq);
  game.rampTo(a.bedGain.gain, want.bed);
  game.rampTo(a.wetGain.gain, want.wet);
  return undefined;
}

/** Ramp a parameter rather than stepping it: a jump in a filter's cutoff is
 *  a click, and this runs on every screen change.
 *
 *  IT READS `game.audio`, NOT `ac()`. `ac()` returns null while muted — it
 *  is the "may I make a sound" gate — so routing the ramp through it meant
 *  muting could not TURN THE BED DOWN: `hushAmbience` ramped nothing and
 *  the bed kept playing at full level. Measured: mute, wait 1.6s, gain
 *  still 0.039. Silencing something is not making a sound. */
export function rampTo(game, param, value) {
  const ac = game.audio;
  if (!ac || !param) return;
  try { param.setTargetAtTime(value, ac.currentTime, 0.4); } catch (err) { /* older API */ }
}

/** Silence the bed without tearing it down, so unmuting picks it up. */
export function hushAmbience(game) {
  if (game.ambLoops) for (const L of game.ambLoops.values()) game.rampTo(L.gain.gain, 0);
  if (game.amb) {
    game.rampTo(game.amb.bedGain.gain, 0);
    game.rampTo(game.amb.wetGain.gain, 0);
  }
  return undefined;
}
